import React, { useEffect, useState } from "react";
import { useFormik, FormikProvider } from "formik";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router";

import CommentForm from "../CommentForm";
import Comment, { CommentSkeleton } from "../Comment";
import { useInfiniteScroll } from "@hooks/useInfiniteScroll";
import { initialValues, validationSchema } from "./form";
import { directions, paginationLimit } from "@const/feed";
import { ReactComponent as IconLive } from "@assets/ic_live.svg";
import {
  createComment,
  getComments,
  getCommentsPortion,
  resetEditCommentId,
} from "@redux/comments/actions";
import { EnterButtonKeyCode } from "@const/common";
import { TABS } from "@components/PostComments/constans";
import LivestreamMessages from "@components/PostComments/components/LivestreamMessages";

import styles from "./index.module.scss";

const PostComments = ({
  id,
  postKey: uniqueKey,
  isCommentActionAvailable,
  isMyPost,
  className,
  children,
  myUsername,
  isLiveStreamChatDisplayed,
  streamUniqueKey,
  postFooterMobile,
}) => {
  const dispatch = useDispatch();
  const { state } = useLocation();
  const { t } = useTranslation("buttons");

  const { allComments, isLoading, totalCount, pendingComments, cursor } =
    useSelector(({ commentsReducer }) => commentsReducer);

  const [activeTab, setActiveTab] = useState(TABS.COMMENTS);

  const getMoreComments = () => {
    dispatch(
      getCommentsPortion({
        direction: directions.previous,
        limit: paginationLimit,
        cursor,
        uniqueKey,
      }),
    );
  };

  const { trigger } = useInfiniteScroll({
    cursor,
    isLoading,
    totalCount,
    direction: directions.next,
    callback: getMoreComments,
  });

  useEffect(() => {
    if (!uniqueKey) return;
    dispatch(
      getComments({
        uniqueKey,
        limit: paginationLimit,
      }),
    );
  }, [uniqueKey]);

  const handleSubmit = ({ message }) => {
    const validatedText = message.trim();

    dispatch(createComment({ id, text: validatedText }));
    formik.setFieldValue("message", "");
  };

  const handleEnter = (event) => {
    const isShift = event.shiftKey;
    const isEnterKDown = event.keyCode === EnterButtonKeyCode;

    if (isEnterKDown && !isShift) {
      event.preventDefault();
      formik.submitForm();
    }
  };

  const formik = useFormik({
    initialValues,
    validationSchema,
    validateOnMount: true,
    onSubmit: handleSubmit,
  });

  useEffect(() => {
    return () => dispatch(resetEditCommentId());
  }, [dispatch]);

  const renderTabs = () => {
    if (!isLiveStreamChatDisplayed) {
      return null;
    }
    return (
      <div className={styles.tabs}>
        {[TABS.COMMENTS, TABS.LIVESTREAM_CHAT].map((type, index) => {
          return (
            <div
              key={type}
              onClick={() => setActiveTab(type)}
              className={classNames(
                activeTab === type ? styles.tabsActive : "",
                "d-flex flex-row align-items-center",
              )}
            >
              {index === 1 ? <IconLive className="me-2 text-primary" /> : null}
              <span className="text-body-2 text-medium">
                {t(type === TABS.COMMENTS ? "COMMENTS" : "LIVE_CHAT_REPLAY")}
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  const renderComments = () => {
    if (activeTab === TABS.LIVESTREAM_CHAT) {
      return null;
    }
    return (
      <>
        {pendingComments.map((commentId) => (
          <CommentSkeleton key={commentId} />
        ))}
        {allComments.map((commentId) => (
          <Comment
            key={commentId}
            isMyPost={isMyPost}
            myUsername={myUsername}
            commentId={commentId}
            postUniqueKey={uniqueKey}
          />
        ))}
        {isLoading && [
          <CommentSkeleton key="comment-skeleton-0" />,
          <CommentSkeleton key="comment-skeleton-1" />,
          <CommentSkeleton key="comment-skeleton-2" />,
          <CommentSkeleton key="comment-skeleton-3" />,
          <CommentSkeleton key="comment-skeleton-4" />,
        ]}
        {trigger}
      </>
    );
  };

  return (
    <>
      {!isLiveStreamChatDisplayed ? (
        <hr className="d-xl-none py-1 inputs-color" />
      ) : null}
      <div className={classNames("flex-column", styles.scroll, className)}>
        {renderTabs()}
        {renderComments()}
        <LivestreamMessages
          activeTab={activeTab}
          streamUniqueKey={streamUniqueKey}
          isLiveStreamChatDisplayed={isLiveStreamChatDisplayed}
        />
      </div>
      {children}
      {isCommentActionAvailable && TABS.COMMENTS === activeTab && (
        <FormikProvider value={formik}>
          <CommentForm
            styles={styles}
            className={className}
            handleEnter={handleEnter}
            postFooterMobile={postFooterMobile}
            withAutofocus={state?.withAutofocus}
          />
        </FormikProvider>
      )}
    </>
  );
};

export default PostComments;
